import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiButton,
  MiLoader,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ISSUE_STATUSES,
  MODAL_TITLES,
} from "@miplatform/constants";
import { formatOpenBalanceDisplay } from "@miplatform/utils";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { IssuePrebill } from "./IssuePrebill";
import { PrebillPaymentInformation } from "./PrebillPaymentInformation/PrebillPaymentInformation";
import { useCloseoutPrebillViewModel } from "./useCloseoutPrebillViewModel";

const getVarianceColor = (variance) => {
  if (variance < 0) {
    return "error.main";
  }
  if (variance > 0) {
    return "success.main";
  }
  return undefined;
};

export const CloseoutPrebill = ({
  stateManager,
  attempt,
  users,
  getAttempt,
  issueIndex,
  setIssueIndex,
  onCancel = () => null,
}) => {
  const { classes, theme } = useStyles();
  const {
    isLoading,
    isSummary,
    isNextDisabled,
    currentIssue,
    currentInvoice,
    invoice,
    invoices,
    selectedSubItem,
    laborRates,
    billingCommitmentsTotal,
    totalPrice,
    variance,
    isIncompleteIssues,
    issues,
    setBillingCommitmentsTotal,
    preBillInvoice,
    getLaborRates,
    handleAddSubItem,
    handleUpdateSubItem,
    handleDeleteSubItem,
    handleCreateInvoiceItem,
    handleGetOrCreateInvoice,
    handleDeleteModalOpen,
    handleDeleteModalClose,
    handleSummaryOpen,
    handleSummaryClose,
  } = useCloseoutPrebillViewModel({
    stateManager,
    issueIndex,
    attempt,
    getAttempt,
    onCancel,
  });

  const handleNext = () => {
    if (issueIndex < issues.length - 1) {
      setIssueIndex((prev) => prev + 1);
    } else if (isSummary) {
      preBillInvoice();
    } else {
      handleSummaryOpen();
    }
  };

  const handleBack = () => {
    if (isSummary) {
      handleSummaryClose();
    } else if (issueIndex > 0) {
      setIssueIndex((prev) => prev - 1);
    } else {
      onCancel();
    }
  };

  const getCancelTitle = () => {
    if (isSummary) {
      return `Return: Pre-Bill (${issues.length}/${issues.length})`;
    }

    if (issueIndex === 0) {
      return "Cancel";
    }

    return "Back to Previous Task";
  };

  const getConfirmTitle = () => {
    if (isSummary) {
      return "Send to Invoicing";
    }

    if (issueIndex < issues.length - 1) {
      return `Start: Pre-Bill (${issueIndex + 2}/${issues.length})`;
    }

    return "Pricing Summary";
  };

  useEffect(() => {
    if (currentIssue && !currentIssue.invoiceItem) {
      handleCreateInvoiceItem();
    }
  }, [currentIssue]);

  useEffect(() => {
    getLaborRates();
  }, []);

  return (
    <Box className={classes.root}>
      <MiLoader
        isLoading={isLoading}
        type="linear"
        style={{ position: "absolute", width: "100%", top: 0, left: 0 }}
      />
      {!isSummary && currentIssue && (
        <>
          <MiAlert
            hideCloseButton
            isOpen={currentIssue.status === ISSUE_STATUSES.CONFIRMED}
            title="Task Incomplete"
            description="This task has not been completed in the field, you are still able to add items, but not invoice."
            variant="error"
            sx={{ mb: theme.spacing(2) }}
          />
          <IssuePrebill
            attempt={attempt}
            laborRates={laborRates}
            issue={currentIssue}
            index={issueIndex}
            users={users}
            invoice={currentInvoice}
            onUpdate={handleUpdateSubItem}
            onDelete={handleDeleteModalOpen}
          />
        </>
      )}
      {isSummary ? (
        <Box display="flex" flexDirection="column" gap={2}>
          <MiAlert
            hideCloseButton
            isOpen={isIncompleteIssues}
            title="Unable to send to invoicing"
            description="All tasks must be completed before the order can be invoiced."
            variant="error"
          />
          <PrebillPaymentInformation
            attempt={attempt}
            invoice={invoice}
            setBillingCommitmentsTotal={setBillingCommitmentsTotal}
            getInvoice={handleGetOrCreateInvoice}
            stateManager={stateManager}
          />
          {issues.map((issue, index) => (
            <IssuePrebill
              key={issue.id}
              attempt={attempt}
              isDeleteDisabled={invoices[index]?.subItems?.length === 1}
              laborRates={laborRates}
              issue={issue}
              index={index}
              users={users}
              invoice={invoices[index]}
              onUpdate={handleUpdateSubItem}
              onDelete={handleDeleteModalOpen}
            />
          ))}
        </Box>
      ) : (
        <Box display="flex" justifyContent="flex-end" py={3}>
          <MiButton
            title="Add Item"
            startIcon={{ path: mdiPlus }}
            variant={BUTTON_VARIANTS.OUTLINED}
            size="small"
            disabled={isLoading}
            onClick={handleAddSubItem}
          />
        </Box>
      )}
      <Box className={classes.footer}>
        {isSummary && (
          <Box display="flex" justifyContent="flex-end" pt={2} pr={4} gap={4}>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Total Price:
            </Typography>
            <Typography fontWeight="600">
              {formatOpenBalanceDisplay(totalPrice)}
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Total Paid:
            </Typography>
            <Typography fontWeight="600">
              {formatOpenBalanceDisplay(billingCommitmentsTotal)}
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Variance:
            </Typography>
            <Typography fontWeight="600" color={getVarianceColor(variance)}>
              {formatOpenBalanceDisplay(variance)}
            </Typography>
          </Box>
        )}
        <Box display="flex" justifyContent="flex-end" gap={4}>
          <MiButton
            className={classes.cancelBtn}
            title={getCancelTitle()}
            variant={BUTTON_VARIANTS.TEXT}
            onClick={handleBack}
            disabled={isLoading}
            style={{
              textDecoration: "underline",
              textUnderlineOffset: theme.spacing(1),
            }}
          />
          <MiButton
            className={classes.confirmBtn}
            title={getConfirmTitle()}
            disabled={isLoading || isNextDisabled}
            variant={BUTTON_VARIANTS.CONTAINED}
            onClick={handleNext}
          />
        </Box>
      </Box>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={Boolean(selectedSubItem)}
        disableClick={isLoading}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={handleDeleteModalClose}
        handlePositive={handleDeleteSubItem}
        description="Are you sure you want to delete this invoice item? This action cannot be undone."
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  footer: {
    backgroundColor: theme.palette.common.white,
    marginTop: "auto",
    position: "sticky",
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    padding: theme.spacing(2, 0),
    zIndex: theme.zIndex.drawer,
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
  },
  confirmBtn: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));
